import { supabase } from './supabase';

// Fetch all messages where the current user is sender or recipient,
// newest first. Returns a flat list; the UI groups into threads.
// Bounded — an unlimited select re-run on every 30s poll would grow with
// every message ever sent instead of just what the thread list needs.
export async function fetchMessages(limit = 500) {
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

// Send a message. sender fields come from the caller (already validated by RLS).
export async function sendMessage({ senderId, senderRole, recipientId, body }) {
  const { data, error } = await supabase
    .from('messages')
    .insert([{
      sender_id: senderId,
      sender_role: senderRole,
      recipient_id: recipientId,
      body: body.trim(),
    }])
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Mark a message read (only the recipient should call this).
export async function markMessageRead(id) {
  const { error } = await supabase
    .from('messages')
    .update({ read: true })
    .eq('id', id);
  if (error) throw error;
}

// Mark multiple messages read in a single request instead of one round
// trip per message — used when opening a thread with several unread
// messages at once.
export async function markMessagesRead(ids) {
  if (!ids || ids.length === 0) return;
  const { error } = await supabase
    .from('messages')
    .update({ read: true })
    .in('id', ids);
  if (error) throw error;
}

// Build a thread list from a flat message array for a given user.
// Each thread is keyed by the "other" participant.
export function groupThreads(messages, myId) {
  const map = new Map();
  for (const m of messages) {
    const otherId = m.sender_id === myId ? m.recipient_id : m.sender_id;
    if (!map.has(otherId)) {
      map.set(otherId, { otherId, messages: [], unread: 0, lastAt: m.created_at });
    }
    const t = map.get(otherId);
    t.messages.push(m);
    if (!m.read && m.recipient_id === myId) t.unread += 1;
    if (m.created_at > t.lastAt) t.lastAt = m.created_at;
  }
  return Array.from(map.values()).sort((a, b) => (b.lastAt > a.lastAt ? 1 : -1));
}
